import type { ComponentType } from 'react';

import type { BrandIconProps } from '@cartwhl/ui/icons/brandIconTypes';

export type PluginPlatform = 'windows' | 'macos';

export interface PluginDownloadOption {
  id: string;
  platform: PluginPlatform;
  platformLabel: string;
  unrealVersion: string;
  versionLabel: string;
  href: string;
}

export interface PluginConfig {
  id: string;
  name: string;
  excitedAbout: string;
  logoWidthClassName: string;
  status: 'available' | 'coming-soon';
  documentationPath?: string;
  downloadOptions?: PluginDownloadOption[];
}

export interface PluginFeature {
  title: string;
  description: string;
}

export const UNREAL_PLUGIN_DOC_PATH =
  '/docs/integrating-cartwheel/unreal-plugin';

const UNREAL_PLUGIN_CDN =
  'https://resources.getcartwheel.com/share/plugins';

const UNREAL_ENGINE_VERSIONS = ['5.6', '5.7', '5.8'] as const;

function createUnrealDownloadOptions(): PluginDownloadOption[] {
  const platforms = [
    { platform: 'windows' as const, platformLabel: 'Windows' },
    { platform: 'macos' as const, platformLabel: 'macOS' },
  ];

  return platforms.flatMap(({ platform, platformLabel }) =>
    UNREAL_ENGINE_VERSIONS.map((version) => {
      const versionSlug = version.replace('.', '');

      return {
        id: `${platform}-${version}`,
        platform,
        platformLabel,
        unrealVersion: version,
        versionLabel: `(UE ${version})`,
        href: `${UNREAL_PLUGIN_CDN}/cartwheel-unreal-ue${versionSlug}-${platform}.zip`,
      };
    }),
  );
}

export const pluginHeroVideoMobile = 'plugins_page_mobile_output.webm';
export const pluginHeroVideoDesktop = 'plugins_page_output.webm';

export const plugins: PluginConfig[] = [
  {
    id: 'unreal-engine',
    name: 'Unreal Engine',
    excitedAbout: 'Unreal Engine Plugin',
    logoWidthClassName: 'h-10 w-10 lg:h-14 lg:w-14',
    status: 'available',
    documentationPath: UNREAL_PLUGIN_DOC_PATH,
    downloadOptions: createUnrealDownloadOptions(),
  },
  {
    id: 'autodesk-maya',
    name: 'Autodesk Maya',
    excitedAbout: 'Autodesk Maya Plugin',
    logoWidthClassName: 'h-10 w-10 lg:h-14 lg:w-14',
    status: 'coming-soon',
  },
  {
    id: 'unity',
    name: 'Unity',
    excitedAbout: 'Unity Plugin',
    logoWidthClassName: 'h-10 w-10 lg:h-14 lg:w-14',
    status: 'coming-soon',
  },
  {
    id: 'blender',
    name: 'Blender',
    excitedAbout: 'Blender Plugin',
    logoWidthClassName: 'h-10 w-10 lg:h-14 lg:w-14',
    status: 'coming-soon',
  },
];

export const pluginFeatures: PluginFeature[] = [
  {
    title: 'Capture',
    description:
      'Capture any performance from any video — multi-character, facial motion, relative scale, and camera moves — in batches of up to 100.',
  },
  {
    title: 'Generate',
    description: 'Generate motion for any character in your Cartwheel library, or entirely new rigged characters.',
  },
  {
    title: 'Retarget',
    description:
      'Retarget captured or generated motion to any character in your Cartwheel library.',
  },
];

export type PluginLogoComponent = ComponentType<BrandIconProps>;
